/**
 * SAVDESIGN — interaction layer
 * Vanilla JS, no dependencies, no globals besides `window.SAVDESIGN`.
 *
 * Everything is lazily initialised through IntersectionObserver and every
 * scroll-driven component shares a single requestAnimationFrame loop that is
 * only running while at least one component is on screen and the tab is
 * visible. Re-entrancy in the Shopify Theme Editor is handled through
 * `shopify:section:load` / `shopify:section:unload`.
 */
(function () {
  'use strict';

  if (window.SAVDESIGN) return; // the asset may be injected twice by sections

  /* ---------------------------------------------------------------------- */
  /* Environment                                                             */
  /* ---------------------------------------------------------------------- */

  var reduceMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointerMQ = window.matchMedia('(hover: hover) and (pointer: fine)');
  var desktopMQ = window.matchMedia('(min-width: 990px)');

  function reduced() {
    return reduceMotionMQ.matches;
  }

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  function range(v, a, b) {
    if (b === a) return v >= b ? 1 : 0;
    return clamp((v - a) / (b - a), 0, 1);
  }

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function easeInOut(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  /* ---------------------------------------------------------------------- */
  /* Shared rAF loop                                                         */
  /* ---------------------------------------------------------------------- */

  var loop = {
    items: new Set(),
    running: false,
    add: function (fn) {
      this.items.add(fn);
      this.start();
    },
    remove: function (fn) {
      this.items.delete(fn);
    },
    start: function () {
      if (this.running || this.items.size === 0 || document.hidden) return;
      this.running = true;
      requestAnimationFrame(this.tick);
    },
    tick: function (now) {
      loop.running = false;
      loop.items.forEach(function (fn) {
        try {
          fn(now);
        } catch (e) {
          /* keep the loop alive if a single component fails */
        }
      });
      loop.start();
    }
  };

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) loop.start();
  });

  /**
   * Registers a callback that only runs while `el` is intersecting.
   * Returns a teardown function.
   */
  function onScreenLoop(el, fn, margin) {
    var active = false;
    var io = new IntersectionObserver(
      function (entries) {
        var visible = entries[0].isIntersecting;
        if (visible && !active) {
          active = true;
          loop.add(fn);
        } else if (!visible && active) {
          active = false;
          loop.remove(fn);
        }
      },
      { rootMargin: margin || '120px 0px 120px 0px' }
    );
    io.observe(el);

    return function () {
      io.disconnect();
      loop.remove(fn);
    };
  }

  /* ---------------------------------------------------------------------- */
  /* Reveal on scroll                                                        */
  /* ---------------------------------------------------------------------- */

  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('sav-in');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );
    return revealObserver;
  }

  function initReveal(root) {
    if (reduced()) {
      root.querySelectorAll('[data-sav-reveal], .sav-line, .sav-step, .sav-eco__phase, .sav-metric, .sav-cta, .sav-labs__item').forEach(function (el) {
        el.classList.add('sav-in');
      });
      return;
    }
    var io = getRevealObserver();
    root.querySelectorAll('[data-sav-reveal], .sav-line, .sav-step, .sav-eco__phase, .sav-metric, .sav-cta, .sav-labs__item').forEach(function (el) {
      if (el.classList.contains('sav-in')) return;
      io.observe(el);
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Counters                                                                */
  /* ---------------------------------------------------------------------- */

  function initCounters(root) {
    var nodes = root.querySelectorAll('[data-sav-count]');
    if (!nodes.length) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          runCounter(entry.target);
        });
      },
      { threshold: 0.35 }
    );

    nodes.forEach(function (el) {
      io.observe(el);
    });
  }

  function runCounter(el) {
    var raw = el.getAttribute('data-sav-count') || el.textContent;
    var match = raw.match(/-?\d[\d.,\s]*/);
    if (!match || reduced()) {
      el.textContent = raw;
      return;
    }

    var numStr = match[0].trim();
    var before = raw.slice(0, match.index);
    var after = raw.slice(match.index + match[0].length);

    // "4.2" / "4,2" -> decimal; "1.200" / "1 200" -> thousands separator
    var decimalMatch = numStr.match(/[.,](\d{1,2})$/);
    var decimals = decimalMatch ? decimalMatch[1].length : 0;
    var normalised = decimals
      ? numStr.slice(0, decimalMatch.index).replace(/[.,\s]/g, '') + '.' + decimalMatch[1]
      : numStr.replace(/[.,\s]/g, '');
    var target = parseFloat(normalised);

    if (isNaN(target)) {
      el.textContent = raw;
      return;
    }

    var start = performance.now();
    var duration = 1700;

    function step(now) {
      var t = clamp((now - start) / duration, 0, 1);
      var value = target * easeOutCubic(t);
      el.textContent = before + value.toFixed(decimals) + after;
      if (t < 1) requestAnimationFrame(step);
      else el.textContent = raw;
    }

    requestAnimationFrame(step);
  }

  /* ---------------------------------------------------------------------- */
  /* Pointer light + micro tilt                                              */
  /* ---------------------------------------------------------------------- */

  function initPointerFx(root) {
    if (!finePointerMQ.matches || reduced()) return;

    root.querySelectorAll('[data-sav-glow], [data-sav-tilt]').forEach(function (el) {
      if (el.__savFx) return;

      var tilt = el.hasAttribute('data-sav-tilt');
      var frame = null;

      function move(event) {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          var rect = el.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          var x = (event.clientX - rect.left) / rect.width;
          var y = (event.clientY - rect.top) / rect.height;
          el.style.setProperty('--sav-mx', (x * 100).toFixed(2) + '%');
          el.style.setProperty('--sav-my', (y * 100).toFixed(2) + '%');
          if (tilt) {
            el.style.setProperty('--sav-tx', (x * 2 - 1).toFixed(3));
            el.style.setProperty('--sav-ty', (y * 2 - 1).toFixed(3));
          }
        });
      }

      function leave() {
        if (frame) cancelAnimationFrame(frame);
        frame = null;
        el.style.setProperty('--sav-tx', '0');
        el.style.setProperty('--sav-ty', '0');
      }

      el.addEventListener('pointermove', move, { passive: true });
      el.addEventListener('pointerleave', leave, { passive: true });
      el.__savFx = function () {
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerleave', leave);
        if (frame) cancelAnimationFrame(frame);
      };
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Magnetic buttons                                                        */
  /* ---------------------------------------------------------------------- */

  function initMagnetic(root) {
    if (!finePointerMQ.matches || reduced()) return;

    root.querySelectorAll('[data-sav-magnetic]').forEach(function (el) {
      if (el.__savMag) return;

      var strength = parseFloat(el.getAttribute('data-sav-magnetic')) || 0.28;
      var frame = null;

      function move(event) {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          var rect = el.getBoundingClientRect();
          var dx = event.clientX - (rect.left + rect.width / 2);
          var dy = event.clientY - (rect.top + rect.height / 2);
          el.style.transform = 'translate3d(' + dx * strength + 'px,' + dy * strength + 'px,0)';
        });
      }

      function leave() {
        if (frame) cancelAnimationFrame(frame);
        frame = null;
        el.style.transform = '';
      }

      el.addEventListener('pointermove', move, { passive: true });
      el.addEventListener('pointerleave', leave, { passive: true });
      el.__savMag = function () {
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerleave', leave);
        if (frame) cancelAnimationFrame(frame);
        el.style.transform = '';
      };
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Marquee                                                                 */
  /* ---------------------------------------------------------------------- */

  function initMarquee(root) {
    root.querySelectorAll('[data-sav-marquee]').forEach(function (el) {
      if (el.__savMarquee) return;

      var track = el.querySelector('.sav-marquee__track');
      var group = el.querySelector('.sav-marquee__group');
      if (!track || !group) return;

      if (reduced()) {
        el.__savMarquee = function () {};
        return;
      }

      var base = Math.abs(parseFloat(el.getAttribute('data-sav-marquee'))) || 40; // px/s
      var direction = el.getAttribute('data-sav-marquee-dir') === 'right' ? 1 : -1;
      var offset = 0;
      var speed = base;
      var target = base;
      var last = 0;
      var width = group.offsetWidth;

      function fill() {
        // Guarantee the track is at least twice the viewport so the loop never
        // shows a gap on ultra-wide screens or with very few items.
        var guard = 0;
        while (
          group.offsetWidth > 0 &&
          track.scrollWidth < el.offsetWidth * 2 + group.offsetWidth &&
          guard < 12
        ) {
          var clone = group.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          track.appendChild(clone);
          guard++;
        }
      }

      function measure() {
        width = group.offsetWidth;
      }

      fill();
      var ro = new ResizeObserver(measure);
      ro.observe(group);

      function tick(now) {
        if (!last) last = now;
        var dt = Math.min((now - last) / 1000, 0.05);
        last = now;
        speed += (target - speed) * 0.08;
        offset += speed * dt * direction;
        if (width > 0) {
          if (offset <= -width) offset += width;
          if (offset >= width) offset -= width;
        }
        track.style.transform = 'translate3d(' + offset.toFixed(2) + 'px,0,0)';
      }

      function slow() {
        target = base * 0.22;
      }
      function normal() {
        target = base;
      }

      el.addEventListener('pointerenter', slow, { passive: true });
      el.addEventListener('pointerleave', normal, { passive: true });

      var stop = onScreenLoop(el, tick);
      el.__savMarquee = function () {
        stop();
        ro.disconnect();
        el.removeEventListener('pointerenter', slow);
        el.removeEventListener('pointerleave', normal);
      };
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Commerce ecosystem — scroll storytelling                                */
  /* ---------------------------------------------------------------------- */

  var ECO_PHASES = [
    [0.0, 0.17],
    [0.15, 0.4],
    [0.36, 0.62],
    [0.58, 0.8],
    [0.78, 1.0]
  ];

  function initEcosystem(root) {
    root.querySelectorAll('[data-sav-eco]').forEach(function (el) {
      if (el.__savEco) return;

      var sticky = el.querySelector('.sav-eco__sticky');
      var phases = Array.prototype.slice.call(el.querySelectorAll('.sav-eco__phase'));
      var steps = Array.prototype.slice.call(el.querySelectorAll('.sav-eco__step i'));
      var shopifyNodes = Array.prototype.slice.call(el.querySelectorAll('.sav-eco__cluster--shopify .sav-eco__node'));
      var tiktokNodes = Array.prototype.slice.call(el.querySelectorAll('.sav-eco__cluster--tiktok .sav-eco__node'));
      var canvas = el.querySelector('.sav-eco__canvas');
      var stage = el.querySelector('.sav-eco__stage');
      var brand = el.querySelector('.sav-eco__core');
      var growth = el.querySelector('.sav-eco__growth');
      if (!sticky) return;

      var flow = canvas && stage && !reduced() && desktopMQ.matches ? createEcoFlow(canvas, stage, brand, shopifyNodes, tiktokNodes, growth) : null;
      var current = -1;
      var lastP = -1;

      function tick() {
        var rect = el.getBoundingClientRect();
        var total = el.offsetHeight - window.innerHeight;
        var p = total > 0 ? clamp(-rect.top / total, 0, 1) : 0;

        if (Math.abs(p - lastP) > 0.0005) {
          lastP = p;

          for (var i = 0; i < ECO_PHASES.length; i++) {
            var v = easeInOut(range(p, ECO_PHASES[i][0], ECO_PHASES[i][1]));
            sticky.style.setProperty('--p' + (i + 1), v.toFixed(4));
            if (steps[i]) steps[i].style.setProperty('--f', v.toFixed(3));
          }

          var index = 0;
          for (var j = ECO_PHASES.length - 1; j >= 0; j--) {
            if (p >= ECO_PHASES[j][0] + (ECO_PHASES[j][1] - ECO_PHASES[j][0]) * 0.25) {
              index = j;
              break;
            }
          }
          if (index !== current) {
            current = index;
            phases.forEach(function (phase, k) {
              phase.classList.toggle('is-active', k === index);
            });
            el.setAttribute('data-phase', index + 1);
          }

          var connect = range(p, ECO_PHASES[1][0], ECO_PHASES[1][1]);
          activateNodes(shopifyNodes, connect);
          activateNodes(tiktokNodes, connect);
        }

        if (flow) flow.render(p);
      }

      // Vertical flow (mobile, and every size under reduced motion): fill the line
      var mline = el.querySelector('.sav-eco__mline i');
      function tickFlow() {
        var rect = el.getBoundingClientRect();
        var vh = window.innerHeight;
        var p = clamp((vh * 0.72 - rect.top) / Math.max(rect.height * 0.86, 1), 0, 1);
        el.style.setProperty('--sav-mp', p.toFixed(3));
      }

      function frame() {
        if (desktopMQ.matches && !reduced()) tick();
        else if (mline) tickFlow();
      }

      var stop = onScreenLoop(el, frame, '200px 0px 200px 0px');
      frame();

      el.__savEco = function () {
        stop();
        if (flow) flow.destroy();
      };
    });
  }

  function activateNodes(nodes, progress) {
    var reveal = progress * (nodes.length + 1);
    nodes.forEach(function (node, i) {
      node.classList.toggle('is-live', reveal > i + 0.4);
    });
  }

  /**
   * Canvas layer: bezier connections + data particles.
   * Positions are cached and only recomputed on resize.
   */
  function createEcoFlow(canvas, stage, brand, leftNodes, rightNodes, growth) {
    var ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return null;

    var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    var paths = [];
    var particles = [];
    var w = 0;
    var h = 0;
    var MAX = 42;

    function center(el, base) {
      var r = el.getBoundingClientRect();
      return { x: r.left - base.left + r.width / 2, y: r.top - base.top + r.height / 2 };
    }

    function measure() {
      var rect = stage.getBoundingClientRect();
      w = Math.max(rect.width, 1);
      h = Math.max(rect.height, 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      var hub = brand ? center(brand, rect) : { x: w / 2, y: h / 2 };
      paths = [];

      leftNodes.forEach(function (node) {
        var p = center(node, rect);
        paths.push(makePath(p, hub, 'left'));
      });
      rightNodes.forEach(function (node) {
        var p = center(node, rect);
        paths.push(makePath(p, hub, 'right'));
      });

      if (growth) {
        var g = center(growth, rect);
        paths.push(makePath(hub, { x: g.x, y: g.y - 10 }, 'down'));
      }

      particles = [];
      for (var i = 0; i < MAX; i++) {
        particles.push({
          path: i % Math.max(paths.length, 1),
          t: Math.random(),
          speed: 0.16 + Math.random() * 0.26,
          size: 0.9 + Math.random() * 1.4
        });
      }
    }

    function makePath(from, to, kind) {
      var mx = (from.x + to.x) / 2;
      var my = (from.y + to.y) / 2;
      var cx;
      var cy;
      if (kind === 'down') {
        cx = mx + (Math.random() - 0.5) * 40;
        cy = my;
      } else {
        cx = mx;
        cy = my + (from.y - to.y) * 0.42 + (kind === 'left' ? -18 : 18);
      }
      return { from: from, to: to, cx: cx, cy: cy, kind: kind };
    }

    function pointAt(path, t) {
      var mt = 1 - t;
      return {
        x: mt * mt * path.from.x + 2 * mt * t * path.cx + t * t * path.to.x,
        y: mt * mt * path.from.y + 2 * mt * t * path.cy + t * t * path.to.y
      };
    }

    var ro = new ResizeObserver(measure);
    ro.observe(stage);
    measure();

    var lastTime = 0;

    function render(p) {
      if (!w || !h) return;
      var now = performance.now();
      var dt = lastTime ? Math.min((now - lastTime) / 1000, 0.05) : 0;
      lastTime = now;

      ctx.clearRect(0, 0, w, h);

      var connect = easeInOut(range(p, ECO_PHASES[1][0], ECO_PHASES[1][1]));
      var hubIn = easeInOut(range(p, ECO_PHASES[2][0], ECO_PHASES[2][1]));
      var out = easeInOut(range(p, ECO_PHASES[4][0], ECO_PHASES[4][1]));
      if (connect <= 0.001) return;

      for (var i = 0; i < paths.length; i++) {
        var path = paths[i];
        var isOut = path.kind === 'down';
        var amount = isOut ? out : Math.min(1, connect * 1.15);
        if (amount <= 0.001) continue;

        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        ctx.lineWidth = 1;
        var grad = ctx.createLinearGradient(path.from.x, path.from.y, path.to.x, path.to.y);
        if (isOut) {
          grad.addColorStop(0, 'rgba(255,255,255,0.42)');
          grad.addColorStop(1, 'rgba(53,224,242,0.05)');
        } else if (path.kind === 'left') {
          grad.addColorStop(0, 'rgba(74,222,155,' + (0.34 * amount).toFixed(3) + ')');
          grad.addColorStop(1, 'rgba(255,255,255,' + (0.12 * amount).toFixed(3) + ')');
        } else {
          grad.addColorStop(0, 'rgba(255,46,136,' + (0.34 * amount).toFixed(3) + ')');
          grad.addColorStop(1, 'rgba(255,255,255,' + (0.12 * amount).toFixed(3) + ')');
        }
        ctx.strokeStyle = grad;

        ctx.beginPath();
        ctx.moveTo(path.from.x, path.from.y);
        var end = amount;
        var steps = 22;
        for (var s = 1; s <= steps; s++) {
          var t = (s / steps) * end;
          var pt = pointAt(path, t);
          ctx.lineTo(pt.x, pt.y);
        }
        ctx.stroke();
        ctx.restore();
      }

      // Data particles
      var flowAmount = Math.max(connect, out);
      if (flowAmount > 0.08 && paths.length) {
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        for (var k = 0; k < particles.length; k++) {
          var particle = particles[k];
          var target = paths[particle.path % paths.length];
          if (!target) continue;
          var live = target.kind === 'down' ? out : connect;
          if (live < 0.1) continue;

          particle.t += particle.speed * dt;
          if (particle.t > 1) particle.t -= 1;

          var tt = particle.t * live;
          var pos = pointAt(target, tt);
          var alpha = 0.9 * live * Math.sin(Math.PI * Math.min(particle.t, 0.999));
          var color =
            target.kind === 'left'
              ? '74,222,155'
              : target.kind === 'right'
              ? '255,46,136'
              : '255,255,255';
          ctx.fillStyle = 'rgba(' + color + ',' + alpha.toFixed(3) + ')';
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, particle.size + hubIn * 0.4, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    }

    return {
      render: render,
      destroy: function () {
        ro.disconnect();
        ctx.clearRect(0, 0, w, h);
      }
    };
  }

  /* ---------------------------------------------------------------------- */
  /* Services — active item tracking                                         */
  /* ---------------------------------------------------------------------- */

  function initServices(root) {
    root.querySelectorAll('[data-sav-services]').forEach(function (el) {
      if (el.__savServices) return;

      var items = Array.prototype.slice.call(el.querySelectorAll('.sav-service'));
      var counter = el.querySelector('[data-sav-service-counter]');
      var total = el.querySelector('[data-sav-service-total]');
      if (!items.length) return;

      if (total) total.textContent = String(items.length).padStart(2, '0');

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            items.forEach(function (item) {
              item.classList.toggle('is-current', item === entry.target);
            });
            if (counter) {
              counter.textContent = String(items.indexOf(entry.target) + 1).padStart(2, '0');
            }
          });
        },
        { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
      );

      items.forEach(function (item) {
        io.observe(item);
      });
      items[0].classList.add('is-current');

      el.__savServices = function () {
        io.disconnect();
      };
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Process — timeline fill                                                 */
  /* ---------------------------------------------------------------------- */

  function initProcess(root) {
    root.querySelectorAll('[data-sav-process]').forEach(function (el) {
      if (el.__savProcess) return;

      var track = el.querySelector('.sav-process__track');
      if (!track) return;

      function tick() {
        var rect = track.getBoundingClientRect();
        var vh = window.innerHeight;
        var p = clamp((vh * 0.72 - rect.top) / Math.max(rect.height * 0.8, 1), 0, 1);
        el.style.setProperty('--sav-pp', p.toFixed(3));
      }

      var stop = onScreenLoop(el, tick);
      tick();
      el.__savProcess = function () {
        stop();
      };
    });
  }

  /* ---------------------------------------------------------------------- */
  /* SavFilters — portfolio category filtering                               */
  /* ---------------------------------------------------------------------- */

  function initFilters(root) {
    root.querySelectorAll('[data-sav-filters]').forEach(function (bar) {
      if (bar.__savFilters) return;

      var scope = bar.closest('.sav');
      if (!scope) return;
      var grid = scope.querySelector('[data-sav-filter-target]');
      if (!grid) return;

      var empty = scope.querySelector('[data-sav-filter-empty]');
      var chips = Array.prototype.slice.call(bar.querySelectorAll('[data-filter]'));
      var items = Array.prototype.slice.call(grid.children);

      function apply(filter) {
        var shown = 0;

        items.forEach(function (item) {
          var cats = (item.getAttribute('data-categories') || '').split(/\s+/);
          var match = filter === 'all' || cats.indexOf(filter) !== -1;
          item.hidden = !match;
          if (match) {
            shown++;
            // replay the entrance so filtering feels like a transition
            item.classList.remove('sav-in');
            void item.offsetWidth;
            item.classList.add('sav-in');
          }
        });

        chips.forEach(function (chip) {
          var active = chip.getAttribute('data-filter') === filter;
          chip.classList.toggle('is-active', active);
          chip.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        if (empty) empty.hidden = shown !== 0;
      }

      function onClick(event) {
        var chip = event.target.closest('[data-filter]');
        if (!chip || !bar.contains(chip)) return;
        apply(chip.getAttribute('data-filter'));
      }

      bar.addEventListener('click', onClick);
      bar.__savFilters = function () {
        bar.removeEventListener('click', onClick);
      };
    });
  }

  /* ---------------------------------------------------------------------- */
  /* SavDepth — parallax planes (mobile)                                     */
  /* ---------------------------------------------------------------------- */

  /**
   * Publishes a -1..1 position signal per element. CSS decides how far each
   * plane actually moves, so the amplitude stays a design decision.
   */
  function initDepth(root) {
    if (reduced() || desktopMQ.matches) return;

    root.querySelectorAll('[data-sav-depth]').forEach(function (el) {
      if (el.__savDepth) return;

      function tick() {
        var rect = el.getBoundingClientRect();
        var vh = window.innerHeight;
        var travelled = (vh - rect.top) / (vh + rect.height);
        el.style.setProperty('--sav-depth', ((0.5 - travelled) * 2).toFixed(3));
      }

      var stop = onScreenLoop(el, tick, '80px 0px 80px 0px');
      tick();
      el.__savDepth = stop;
    });
  }

  /* ---------------------------------------------------------------------- */
  /* SavHero — mobile core drift                                             */
  /* ---------------------------------------------------------------------- */

  function initHero(root) {
    root.querySelectorAll('.sav-hero').forEach(function (el) {
      if (el.__savHero) return;
      if (reduced()) return;

      function tick() {
        var h = el.offsetHeight || 1;
        el.style.setProperty('--sav-scroll-y', clamp(window.scrollY / h, 0, 1).toFixed(3));
      }

      var stop = onScreenLoop(el, tick);
      tick();
      el.__savHero = stop;
    });
  }

  /* ---------------------------------------------------------------------- */
  /* SavStickyCta — appears after a real scroll, retreats near the end       */
  /* ---------------------------------------------------------------------- */

  var stickyCta = null;

  function initStickyCta() {
    if (stickyCta) return;
    var source = document.querySelector('[data-sav-sticky-cta]');
    if (!source) return;

    var label = source.getAttribute('data-label') || '';
    var href = source.getAttribute('data-href') || '';
    // A pill with no destination looks exactly like a working button and does
    // nothing when tapped, which is worse than no pill at all. If the merchant
    // leaves the link empty, simply don't build it.
    if (!label || !href) return;

    var el = document.createElement('a');
    el.className = 'sav-sticky-cta';
    el.innerHTML =
      '<span></span><svg viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
      '<path d="M1 8h13M9 3l5 5-5 5" stroke="currentColor" stroke-width="1.4" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    el.querySelector('span').textContent = label;
    el.setAttribute('href', href);
    document.body.appendChild(el);
    stickyCta = el;

    // Anything already asking for the same action makes the pill redundant.
    // Scoped to our own zones on purpose: a bare `form` selector also matched
    // the base theme's search-modal forms, which sit at 0x0 in the top left and
    // can read as permanently intersecting, pinning the pill off screen forever.
    var visibleZones = new Set();
    var endZones = document.querySelectorAll('.sav-cta, .sav-footer, .sav-contact, form.sav-form');

    if (endZones.length) {
      var zoneObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            // A collapsed element is not a zone the visitor can actually see,
            // and some browsers report zero-area rects as intersecting.
            var box = entry.boundingClientRect;
            var real = box.width > 0 && box.height > 0;
            if (entry.isIntersecting && real) visibleZones.add(entry.target);
            else visibleZones.delete(entry.target);
          });
          update();
        },
        { rootMargin: '0px 0px -10% 0px' }
      );
      endZones.forEach(function (zone) {
        zoneObserver.observe(zone);
      });
    }

    var ticking = false;

    function update() {
      ticking = false;
      var past = window.scrollY > window.innerHeight * 1.2;
      el.classList.toggle('is-on', past && visibleZones.size === 0);
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    // The state above is only ever computed inside requestAnimationFrame, which
    // browsers freeze in a background tab. Returning to the tab, or restoring
    // the page from the back/forward cache (routine on iOS Safari), would leave
    // the pill stuck in whatever state it held when it froze. Recompute directly
    // on both, and on resize, where the 1.2 x viewport threshold moves.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) update();
    });
    window.addEventListener('pageshow', update);
    window.addEventListener('resize', update, { passive: true });

    update();
  }

  /* ---------------------------------------------------------------------- */
  /* SavSplit — index children of split reveals                              */
  /* ---------------------------------------------------------------------- */

  function initSplit(root) {
    root.querySelectorAll('[data-sav-reveal="split"]').forEach(function (el) {
      if (el.__savSplit) return;
      Array.prototype.forEach.call(el.children, function (child, i) {
        child.style.setProperty('--sav-i', i);
      });
      el.__savSplit = true;
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Custom cursor                                                           */
  /* ---------------------------------------------------------------------- */

  var cursor = null;

  function initCursor() {
    if (cursor || !finePointerMQ.matches || reduced()) return;
    if (!document.querySelector('[data-sav-cursor]')) return;

    var dot = document.createElement('div');
    dot.className = 'sav-cursor';
    var label = document.createElement('div');
    label.className = 'sav-cursor__label';
    var labelText = document.createElement('span');
    label.appendChild(labelText);
    document.body.appendChild(dot);
    document.body.appendChild(label);
    document.documentElement.classList.add('sav-cursor-active');

    var x = window.innerWidth / 2;
    var y = window.innerHeight / 2;
    var lx = x;
    var ly = y;
    var running = false;

    function move(event) {
      x = event.clientX;
      y = event.clientY;
      if (!dot.classList.contains('is-on')) dot.classList.add('is-on');
      if (!running) {
        running = true;
        requestAnimationFrame(frame);
      }

      var hit = event.target instanceof Element ? event.target.closest('[data-sav-cursor-label], a, button') : null;
      var text = hit && hit.getAttribute ? hit.getAttribute('data-sav-cursor-label') : null;

      if (text) {
        labelText.textContent = text;
        label.classList.add('is-on');
        dot.classList.remove('is-link');
      } else {
        label.classList.remove('is-on');
        dot.classList.toggle('is-link', !!hit);
      }
    }

    function frame() {
      lx += (x - lx) * 0.22;
      ly += (y - ly) * 0.22;
      dot.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
      label.style.transform = 'translate3d(' + lx + 'px,' + ly + 'px,0)';
      if (Math.abs(x - lx) > 0.2 || Math.abs(y - ly) > 0.2) {
        requestAnimationFrame(frame);
      } else {
        running = false;
      }
    }

    function leave() {
      dot.classList.remove('is-on');
      label.classList.remove('is-on');
    }

    window.addEventListener('pointermove', move, { passive: true });
    document.addEventListener('pointerleave', leave, { passive: true });

    cursor = { dot: dot, label: label };
  }

  /* ---------------------------------------------------------------------- */
  /* Scroll progress                                                         */
  /* ---------------------------------------------------------------------- */

  var progressBar = null;

  function initProgress() {
    if (progressBar || reduced()) return;
    if (!desktopMQ.matches) return;
    if (!document.querySelector('[data-sav-progress]')) return;

    var wrap = document.createElement('div');
    wrap.className = 'sav-progress';
    wrap.setAttribute('aria-hidden', 'true');
    var bar = document.createElement('span');
    bar.className = 'sav-progress__bar';
    wrap.appendChild(bar);
    document.body.appendChild(wrap);
    progressBar = wrap;

    var ticking = false;

    function update() {
      ticking = false;
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var p = max > 0 ? clamp(window.scrollY / max, 0, 1) : 0;
      wrap.style.setProperty('--sav-scroll', p.toFixed(4));
      wrap.classList.toggle('is-on', window.scrollY > 200);
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* ---------------------------------------------------------------------- */
  /* Scan / teardown                                                         */
  /* ---------------------------------------------------------------------- */

  var TEARDOWN_KEYS = [
    '__savFx',
    '__savMag',
    '__savMarquee',
    '__savEco',
    '__savServices',
    '__savProcess',
    '__savDepth',
    '__savHero',
    '__savFilters'
  ];

  function scan(root) {
    var scope = root || document;
    initSplit(scope);
    initReveal(scope);
    initCounters(scope);
    initPointerFx(scope);
    initMagnetic(scope);
    initMarquee(scope);
    initEcosystem(scope);
    initServices(scope);
    initProcess(scope);
    initDepth(scope);
    initHero(scope);
    initFilters(scope);
    initCursor();
    initProgress();
    initStickyCta();
  }

  function destroy(root) {
    if (!root || !root.querySelectorAll) return;
    var all = [root].concat(Array.prototype.slice.call(root.querySelectorAll('*')));
    all.forEach(function (el) {
      TEARDOWN_KEYS.forEach(function (key) {
        if (typeof el[key] === 'function') {
          el[key]();
          el[key] = null;
        }
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Boot                                                                    */
  /* ---------------------------------------------------------------------- */

  function boot() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme Editor support
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  document.addEventListener('shopify:section:reorder', function () {
    scan(document);
  });

  // Re-evaluate motion / breakpoint preferences on the fly: components whose
  // behaviour differs between mobile and desktop are rebuilt, not patched.
  var rebuildTimer = null;
  function rebuild() {
    clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(function () {
      destroy(document.body);
      scan(document);
    }, 200);
  }

  reduceMotionMQ.addEventListener('change', rebuild);
  desktopMQ.addEventListener('change', rebuild);

  window.SAVDESIGN = {
    scan: scan,
    destroy: destroy,
    version: '1.0.0'
  };
})();
